import React, { useState, useEffect } from "react";
import { CopyCheck } from "lucide-react";
import States from "../../constant/states"; // Assuming this path is correct

const PartyForm = ({
  initialValues,
  onSubmit,
  onCancel,
  isEditMode = false,
}) => {
  // Core party details
  const [name, setName] = useState(initialValues?.name || "");
  const [email, setEmail] = useState(initialValues?.email || "");
  const [mobile, setMobile] = useState(initialValues?.mobile || "");
  const [partyType, setPartyType] = useState(initialValues?.partyType || "");
  const [isActive, setIsActive] = useState(
    initialValues?.isActive === undefined ? true : initialValues.isActive
  );

  // --- New state for combined amount ---
  const [amount, setAmount] = useState(0);
  const [amountType, setAmountType] = useState("toCollect"); // 'toCollect' or 'toPay'
  // --- End new state ---

  // Address details
  const [sameAsBilling, setSameAsBilling] = useState(false); // Start with false
  const [billingAddress, setBillingAddress] = useState(
    initialValues?.billingAddress || {
      name: "",
      streetAddress: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      gstNo: "",
    }
  );
  const [shippingAddress, setShippingAddress] = useState(
    initialValues?.shippingAddress || {
      name: "",
      streetAddress: "",
      city: "",
      state: "",
      country: "India",
      pincode: "",
      gstNo: "",
    }
  );

  // Form state
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({}); // State to hold field-specific errors

  // Effect to initialize form fields from initialValues
  useEffect(() => {
    if (initialValues) {
      setName(initialValues.name || "");
      setEmail(initialValues.email || "");
      setMobile(initialValues.mobile || "");
      setPartyType(initialValues.partyType || "");
      setIsActive(
        initialValues.isActive === undefined ? true : initialValues.isActive
      );

      // --- Initialize combined amount state ---
      if (initialValues.toCollect > 0) {
        setAmount(initialValues.toCollect);
        setAmountType("toCollect");
      } else if (initialValues.toPay > 0) {
        setAmount(initialValues.toPay);
        setAmountType("toPay");
      } else {
        setAmount(0);
        setAmountType("toCollect"); // Default if both are 0 or undefined
      }
      // --- End combined amount initialization ---

      const initialBilling = initialValues.billingAddress || { name: "", streetAddress: "", city: "", state: "", country: "India", pincode: "", gstNo: "" };
      const initialShipping = initialValues.shippingAddress || { name: "", streetAddress: "", city: "", state: "", country: "India", pincode: "", gstNo: "" };

      setBillingAddress(initialBilling);
      setShippingAddress(initialShipping);

      // Check if addresses are identical to pre-check "sameAsBilling"
      const addressesAreSame = JSON.stringify(initialBilling) === JSON.stringify(initialShipping) && initialBilling.name !== ""; // Check if they are identical *and* billing has some data
      setSameAsBilling(addressesAreSame);

      // GST Handling - simplified, assumes either both or one is present
      if (initialBilling.gstNo) {
        setBillingAddress(prev => ({ ...prev, gstNo: initialBilling.gstNo }));
      }
      if (initialShipping.gstNo && !addressesAreSame) { // Only set shipping GST if different
        setShippingAddress(prev => ({ ...prev, gstNo: initialShipping.gstNo }));
      } else if (addressesAreSame && initialBilling.gstNo) {
        setShippingAddress(prev => ({ ...prev, gstNo: initialBilling.gstNo })); // If same, copy billing GST
      }


    } else {
      // Reset form if initialValues becomes null/undefined (e.g., switching from edit to add)
      setName("");
      setEmail("");
      setMobile("");
      setPartyType("");
      setIsActive(true);
      setAmount(0);
      setAmountType("toCollect");
      setBillingAddress({ name: "", streetAddress: "", city: "", state: "", country: "India", pincode: "", gstNo: "" });
      setShippingAddress({ name: "", streetAddress: "", city: "", state: "", country: "India", pincode: "", gstNo: "" });
      setSameAsBilling(false);
      setErrors({});
    }
  }, [initialValues]);


  // Validation functions (unchanged)
  const isValidEmail = (email) => /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email);
  const isValidMobile = (mobile) => /^(?:\+91[\-\s]?)?[6-9]\d{9}$/.test(mobile);
  const isValidGst = (gst) => /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(String(gst || '').toUpperCase()); // Added null check

  const validateForm = () => {
    let tempErrors = {};

    if (!name) tempErrors.name = "Name is required";
    if (!email) tempErrors.email = "Email is required";
    if (email && !isValidEmail(email)) tempErrors.email = "Please enter a valid email address";
    if (!mobile) tempErrors.mobile = "Mobile is required";
    if (mobile && !isValidMobile(mobile)) tempErrors.mobile = "Please enter a valid 10-digit mobile number";
    if (!partyType) tempErrors.partyType = "Party Type is required";

    // Amount validation (optional, e.g., ensure non-negative)
    if (amount < 0) tempErrors.amount = "Amount cannot be negative.";
    // No need to validate amountType if it has a default and no blank option

    // Billing Address Validation
    if (!billingAddress.name) tempErrors["billingAddress.name"] = "Billing Name is required";
    if (!billingAddress.streetAddress) tempErrors["billingAddress.streetAddress"] = "Billing Street Address is required";
    if (!billingAddress.city) tempErrors["billingAddress.city"] = "Billing City is required";
    if (!billingAddress.state) tempErrors["billingAddress.state"] = "Billing State is required";
    if (!billingAddress.country) tempErrors["billingAddress.country"] = "Billing Country is required";
    if (!billingAddress.pincode) tempErrors["billingAddress.pincode"] = "Billing Pincode is required";
    if (billingAddress.gstNo && !isValidGst(billingAddress.gstNo)) tempErrors["billingAddress.gstNo"] = "Please enter a valid GST number for Billing Address"; // Allow empty GST

    // Shipping Address Validation (only if not same as billing)
    if (!sameAsBilling) {
      if (!shippingAddress.name) tempErrors["shippingAddress.name"] = "Shipping Name is required";
      if (!shippingAddress.streetAddress) tempErrors["shippingAddress.streetAddress"] = "Shipping Street Address is required";
      if (!shippingAddress.city) tempErrors["shippingAddress.city"] = "Shipping City is required";
      if (!shippingAddress.state) tempErrors["shippingAddress.state"] = "Shipping State is required";
      if (!shippingAddress.country) tempErrors["shippingAddress.country"] = "Shipping Country is required";
      if (!shippingAddress.pincode) tempErrors["shippingAddress.pincode"] = "Shipping Pincode is required";
      if (shippingAddress.gstNo && !isValidGst(shippingAddress.gstNo)) tempErrors["shippingAddress.gstNo"] = "Please enter a valid GST number for Shipping Address"; // Allow empty GST
    }

    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0; // Return true if no errors
  };

  const handleMobileChange = (e) => {
    const value = e.target.value;
    setMobile(value);

    if (value && !isValidMobile(value)) {
      setErrors(prev => ({ ...prev, mobile: "Please enter a valid 10-digit mobile number" }));
    } else {
      setErrors(prev => {
        const { mobile, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleBillingAddressChange = (field, value) => {
    const newBillingAddress = { ...billingAddress, [field]: value };
    setBillingAddress(newBillingAddress);
    if (sameAsBilling) {
      setShippingAddress({ ...newBillingAddress });
    }

    if (field === 'gstNo') {
      if (value && !isValidGst(value)) {
        setErrors(prev => ({ ...prev, "billingAddress.gstNo": "Please enter a valid GST number" }));
      } else {
        setErrors(prev => {
          const { "billingAddress.gstNo": _, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleShippingAddressChange = (field, value) => {
    setShippingAddress(prev => ({ ...prev, [field]: value }));

    if (field === 'gstNo') {
      if (value && !isValidGst(value)) {
        setErrors(prev => ({ ...prev, "shippingAddress.gstNo": "Please enter a valid GST number" }));
      } else {
        setErrors(prev => {
          const { "shippingAddress.gstNo": _, ...rest } = prev;
          return rest;
        });
      }
    }
  };


  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      return; // Stop submission if validation fails
    }

    setLoading(true);
    try {
      const partyData = {
        name,
        email,
        mobile,
        // --- Set toCollect and toPay based on amount and amountType ---
        toCollect: amountType === "toCollect" ? Number(amount) : 0,
        toPay: amountType === "toPay" ? Number(amount) : 0,
        // --- End setting amounts ---
        partyType,
        isActive,
        // Ensure GST is an empty string if not provided, instead of null/undefined
        billingAddress: {
          ...billingAddress,
          gstNo: billingAddress.gstNo || ""
        },
        shippingAddress: sameAsBilling
          ? { ...billingAddress, gstNo: billingAddress.gstNo || "" } // Use cleaned billing address
          : { ...shippingAddress, gstNo: shippingAddress.gstNo || "" }, // Use cleaned shipping address
      };

      await onSubmit(partyData);
    } catch (err) {
      console.error("Form submission error:", err); // Log the error for debugging
      setErrors({ form: err.message || "Failed to submit the form. Please check console for details." });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  const copyBillingToShipping = () => {
    const isCurrentlySame = !sameAsBilling; // The new state value
    setSameAsBilling(isCurrentlySame);
    if (isCurrentlySame) {
      // Copy current billing state to shipping state
      setShippingAddress({ ...billingAddress });
    }
    // No need for else block, shippingAddress retains its independent values when unchecked
  };



  return (
    <div className="bg-white rounded-lg shadow-sm">
      {/* General Form Error */}
      {errors.form && (
        <div className="bg-red-50 text-red-700 p-4 rounded-lg mb-6 mx-6">
          {errors.form}
        </div>
      )}

      <div className="p-6 space-y-6">
        {/* General Info Section */}
        <div className="grid bg-gray-50 p-6 rounded-lg grid-cols-1 md:grid-cols-2 gap-6">
          {/* Name */}
          <div>
            <label className="block capitalize text-sm font-medium text-gray-700 mb-2">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              //   disabled={isEditMode === true} // Name might be editable even in edit mode? Adjust if needed.
              className={`w-full px-4 py-2 capitalize border ${errors.name ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={`w-full px-4 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Enter email address"
            />
            {errors.email && (
              <p className="mt-1 text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Mobile */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mobile <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={mobile}
              onChange={handleMobileChange} // Replace the old onChange
              required
              className={`w-full px-4 py-2 border ${errors.mobile ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              placeholder="Enter 10-digit mobile"
            />

            {errors.mobile && (
              <p className="mt-1 text-sm text-red-500">{errors.mobile}</p>
            )}
          </div>

          {/* Party Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Party Type <span className="text-red-500">*</span>
            </label>
            <select
              value={partyType}
              onChange={(e) => setPartyType(e.target.value)}
              required
              //   disabled={isEditMode === true} // Usually party type doesn't change after creation
              className={`w-full px-4 py-2 border ${errors.partyType ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
            >
              <option value="">Select Party Type</option>
              <option value="supplier">Supplier</option>
              <option value="customer">Customer</option>
            </select>
            {errors.partyType && (
              <p className="mt-1 text-sm text-red-500">{errors.partyType}</p>
            )}
          </div>

          {/* --- Combined Amount/Type Section --- */}
          {!isEditMode && (
            <>
              {/* Amount Type Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opening Balance Type
                </label>
                <select
                  value={amountType}
                  onChange={(e) => setAmountType(e.target.value)}
                  disabled={isEditMode === true} // Already checked by !isEditMode, but keeps logic clear
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                >
                  <option value="toCollect">To Collect</option>
                  <option value="toPay">To Pay</option>
                </select>
                {/* No error display needed unless adding validation */}
              </div>

              {/* Amount Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Opening Balance Amount
                </label>
                <input
                  type="number"
                  value={amount || ""} // Ensure controlled input
                  min="0" // Prevent negative numbers directly in input
                  onChange={(e) => setAmount(parseFloat(e.target.value) || 0)} // Ensure state holds a number
                  disabled={isEditMode === true} // Already checked by !isEditMode
                  className={`w-full px-4 py-2 border ${errors.amount ? "border-red-500" : "border-gray-300"
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  placeholder="Enter amount"
                />
                {errors.amount && (
                  <p className="mt-1 text-sm text-red-500">{errors.amount}</p>
                )}
              </div>
            </>
          )}
          {/* --- End Combined Amount/Type Section --- */}


          {/* Status (Only in Edit Mode) */}
          {isEditMode && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status
              </label>
              <select
                value={isActive ? "Active" : "Pending"} // Or whatever your status values are
                onChange={(e) => setIsActive(e.target.value === "Active")}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="Active">Active</option>
                <option value="Pending">Pending</option> {/* Adjust options as needed */}
              </select>
            </div>
          )}
        </div> {/* End General Info Section */}


        {/* --- Billing Address --- */}
        <div className="mt-8 bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Billing Address
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Billing Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={billingAddress.name}
                onChange={(e) => handleBillingAddressChange('name', e.target.value)}
                className={`w-full px-4 py-2 border ${errors["billingAddress.name"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors["billingAddress.name"] && (<p className="mt-1 text-sm text-red-500">{errors["billingAddress.name"]}</p>)}
            </div>

            {/* Billing Street Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={billingAddress.streetAddress}
                onChange={(e) => handleBillingAddressChange('streetAddress', e.target.value)}
                className={`w-full px-4 py-2 border ${errors["billingAddress.streetAddress"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors["billingAddress.streetAddress"] && (<p className="mt-1 text-sm text-red-500">{errors["billingAddress.streetAddress"]}</p>)}
            </div>

            {/* Billing City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={billingAddress.city}
                onChange={(e) => handleBillingAddressChange('city', e.target.value)}
                className={`w-full px-4 py-2 border ${errors["billingAddress.city"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors["billingAddress.city"] && (<p className="mt-1 text-sm text-red-500">{errors["billingAddress.city"]}</p>)}
            </div>

            {/* Billing State */}
            <div>
              <States
                label="State"
                required // Make States component accept/handle required prop if needed visually
                value={billingAddress.state}
                onChange={(e) => handleBillingAddressChange('state', e.target.value)}
                error={errors["billingAddress.state"]}
              />
              {/* Error message displayed by States component or below */}
              {errors["billingAddress.state"] && !errors["States.state"] /* Prevent duplicate messages */ && (
                <p className="mt-1 text-sm text-red-500">{errors["billingAddress.state"]}</p>
              )}
            </div>

            {/* Billing Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={billingAddress.country}
                onChange={(e) => handleBillingAddressChange('country', e.target.value)}
                // Consider making this non-editable or a dropdown if always "India"
                // disabled={true}
                className={`w-full px-4 py-2 border ${errors["billingAddress.country"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors["billingAddress.country"] && (<p className="mt-1 text-sm text-red-500">{errors["billingAddress.country"]}</p>)}
            </div>

            {/* Billing Pincode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required
                value={billingAddress.pincode}
                onChange={(e) => handleBillingAddressChange('pincode', e.target.value)}
                className={`w-full px-4 py-2 border ${errors["billingAddress.pincode"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
              />
              {errors["billingAddress.pincode"] && (<p className="mt-1 text-sm text-red-500">{errors["billingAddress.pincode"]}</p>)}
            </div>

            {/* Billing GST No. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GST No.
              </label>
              <input
                type="text"
                value={billingAddress.gstNo}
                onChange={(e) => handleBillingAddressChange('gstNo', e.target.value.toUpperCase())}
                className={`w-full px-4 py-2 border ${errors["billingAddress.gstNo"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                placeholder="Enter GST number"
              />
              {errors["billingAddress.gstNo"] && (<p className="mt-1 text-sm text-red-500">{errors["billingAddress.gstNo"]}</p>)}
            </div>
          </div>
        </div> {/* End Billing Address */}


        {/* Copy Address Button */}
        <div className="flex items-center justify-end mt-2 space-x-2">
          <input
            type="checkbox"
            id="sameAsBillingCheckbox"
            checked={sameAsBilling}
            onChange={copyBillingToShipping}
            className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
          />
          <label htmlFor="sameAsBillingCheckbox" className="text-sm text-gray-700 cursor-pointer select-none">
            Shipping Address is the same as Billing Address
          </label>
        </div>


        {/* --- Shipping Address --- */}
        <div className={`mt-8 bg-gray-50 p-6 rounded-lg ${sameAsBilling ? 'opacity-50 pointer-events-none' : ''}`}> {/* Disable visually and functionally */}
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Shipping Address {sameAsBilling && <span className="text-sm text-gray-500">(Same as Billing)</span>}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Shipping Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required={!sameAsBilling}
                value={shippingAddress.name}
                onChange={(e) => setShippingAddress({ ...shippingAddress, name: e.target.value })}
                disabled={sameAsBilling}
                className={`w-full px-4 py-2 border ${errors["shippingAddress.name"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100`}
              />
              {errors["shippingAddress.name"] && (<p className="mt-1 text-sm text-red-500">{errors["shippingAddress.name"]}</p>)}
            </div>

            {/* Shipping Street Address */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Street Address <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required={!sameAsBilling}
                value={shippingAddress.streetAddress}
                onChange={(e) => setShippingAddress({ ...shippingAddress, streetAddress: e.target.value })}
                disabled={sameAsBilling}
                className={`w-full px-4 py-2 border ${errors["shippingAddress.streetAddress"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100`}
              />
              {errors["shippingAddress.streetAddress"] && (<p className="mt-1 text-sm text-red-500">{errors["shippingAddress.streetAddress"]}</p>)}
            </div>

            {/* Shipping City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                City <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required={!sameAsBilling}
                value={shippingAddress.city}
                onChange={(e) => setShippingAddress({ ...shippingAddress, city: e.target.value })}
                disabled={sameAsBilling}
                className={`w-full px-4 py-2 border ${errors["shippingAddress.city"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100`}
              />
              {errors["shippingAddress.city"] && (<p className="mt-1 text-sm text-red-500">{errors["shippingAddress.city"]}</p>)}
            </div>

            {/* Shipping State */}
            <div>
              <States
                label="State"
                required={!sameAsBilling}
                value={shippingAddress.state}
                onChange={(e) => setShippingAddress({ ...shippingAddress, state: e.target.value })}
                disabled={sameAsBilling}
                error={errors["shippingAddress.state"]}
              />
              {errors["shippingAddress.state"] && !errors["States.state"] && (
                <p className="mt-1 text-sm text-red-500">{errors["shippingAddress.state"]}</p>
              )}
            </div>

            {/* Shipping Country */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Country <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required={!sameAsBilling}
                value={shippingAddress.country}
                onChange={(e) => setShippingAddress({ ...shippingAddress, country: e.target.value })}
                disabled={sameAsBilling}
                className={`w-full px-4 py-2 border ${errors["shippingAddress.country"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100`}
              />
              {errors["shippingAddress.country"] && (<p className="mt-1 text-sm text-red-500">{errors["shippingAddress.country"]}</p>)}
            </div>

            {/* Shipping Pincode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pincode <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                required={!sameAsBilling}
                value={shippingAddress.pincode}
                onChange={(e) => setShippingAddress({ ...shippingAddress, pincode: e.target.value })}
                disabled={sameAsBilling}
                className={`w-full px-4 py-2 border ${errors["shippingAddress.pincode"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100`}
              />
              {errors["shippingAddress.pincode"] && (<p className="mt-1 text-sm text-red-500">{errors["shippingAddress.pincode"]}</p>)}
            </div>

            {/* Shipping GST No. */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                GST No.
              </label>
              <input
                type="text"
                value={shippingAddress.gstNo}
                onChange={(e) => handleShippingAddressChange('gstNo', e.target.value.toUpperCase())}
                disabled={sameAsBilling}
                className={`w-full px-4 py-2 border ${errors["shippingAddress.gstNo"] ? "border-red-500" : "border-gray-300"} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-100`}
                placeholder="Enter GST number"
              />
              {errors["shippingAddress.gstNo"] && (<p className="mt-1 text-sm text-red-500">{errors["shippingAddress.gstNo"]}</p>)}
            </div>
          </div>
        </div> {/* End Shipping Address */}
      </div> {/* End p-6 space-y-6 */}


      {/* Form Actions */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-4 rounded-b-lg sticky bottom-0">
        <button
          type="button"
          onClick={handleCancel}
          className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={loading}
          className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading
            ? isEditMode
              ? "Saving..."
              : "Adding..."
            : isEditMode
              ? "Save Changes"
              : "Add Party"}
        </button>
      </div>
    </div>
  );
};

export default PartyForm;